import {
  forwardRef,
  type KeyboardEvent as ReactKeyboardEvent,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { type Mention } from '.';
import { Button } from '../../../../primitives/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../../primitives/dialog';
import { Input } from '../../../../primitives/input';

import { cn } from '../../../../utils/cn';
import { ComboboxModel } from '../../../combobox/combobox-model';
import { LabelWithoutRef } from '../../../label/label-without-ref';

function validateForm(mention: string) {
  if (!mention.trim()) {
    return 'Debes seleccionar un dato del cliente';
  }
  return '';
}

export interface MentionListRef {
  onKeyDown: (props: { event: ReactKeyboardEvent }) => boolean;
}

interface MentionListProps {
  allItems: string[];
  items: string[];
  command: (item: Mention) => void;
}

export const MentionList = forwardRef<MentionListRef, MentionListProps>(function MentionList(
  { allItems = [], items = [], command },
  ref,
) {
  const model = allItems.map((variable) => ({ value: variable, item: variable }));

  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  /**
   * States for dialog
   */
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState<{ mention: string; defaultValue: string; error: string } | null>(
    null,
  );

  useEffect(() => setSelectedIndex(0), [items]);

  /**
   * Ensures that the currently selected item inside the popover
   * remains visible when navigating with keyboard arrows.
   */
  useEffect(() => {
    const currentSelectedItem = itemRefs.current[selectedIndex];
    if (!currentSelectedItem) return;

    currentSelectedItem.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: 'smooth',
    });
  }, [selectedIndex]);

  const upHandler = () => {
    setSelectedIndex((prev) => (prev + items.length - 1) % items.length);
  };

  const downHandler = () => {
    setSelectedIndex((prev) => (prev + 1) % items.length);
  };

  const enterHandler = () => {
    selectItem(selectedIndex);
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        upHandler();
        return true;
      }

      if (event.key === 'ArrowDown') {
        downHandler();
        return true;
      }

      if (event.key === 'Enter') {
        enterHandler();
        return true;
      }

      return false;
    },
  }));

  function selectItem(index: number) {
    const item = items[index];
    if (!item) return;

    setForm({ mention: item, defaultValue: '', error: '' });
    setOpenDialog(true);
  }

  function handleConfirm() {
    if (!form) return;

    const errorMessage = validateForm(form.mention);
    if (errorMessage) {
      setForm((prev) => (prev ? { ...prev, error: errorMessage } : prev));
      return;
    }

    command({
      mention: form.mention,
      default: form.defaultValue.trim(),
    });

    setOpenDialog(false);
    setForm(null);
  }

  return (
    <div className="overflow-hidden rounded-[1.2rem] border border-input shadow-md">
      <div
        className={cn(
          'relative z-10 max-h-52 w-72 overflow-y-auto rounded-[inherit] bg-popover text-popover-foreground',
          {
            hidden: openDialog,
          },
        )}
        role="listbox"
      >
        {items.length ? (
          <ul className="flex flex-col p-1">
            {items.map((item, index) => (
              <li key={item + index}>
                <button
                  aria-selected={index === selectedIndex}
                  onClick={() => selectItem(index)}
                  ref={(el) => {
                    itemRefs.current[index] = el;
                  }}
                  role="option"
                  className={cn(
                    'mb-1 w-full cursor-default rounded-lg px-2 py-1.5 text-left text-base outline-hidden select-none last:mb-0',
                    'hover:bg-accent hover:text-accent-foreground',
                    'focus:bg-accent focus:text-accent-foreground',
                    index === selectedIndex && 'bg-accent text-accent-foreground',
                  )}
                >
                  <span className="block break-words whitespace-normal">{item}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div
            className="px-3 py-2 text-base text-muted-foreground"
            role="option"
            aria-selected="false"
            aria-disabled="true"
          >
            Sin resultados 🫥
          </div>
        )}
      </div>

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="h-auto max-w-lg rounded-lg md:h-auto">
          <DialogHeader className="mb-8 px-1">
            <DialogTitle className="leading-7">
              Agregar{' '}
              <span className="rounded-3xl bg-accent/50 px-2 py-[0.2rem] text-base font-normal text-accent-foreground">
                {form?.mention}
              </span>
            </DialogTitle>
            <DialogDescription>
              Selecciona un dato del cliente y asigna un valor default que se mostrará cuando el
              dato no exista o no pueda obtenerse.
            </DialogDescription>
          </DialogHeader>

          <section className="grid gap-4 px-1">
            <div className="space-y-1">
              <LabelWithoutRef>Dato del cliente</LabelWithoutRef>
              <ComboboxModel
                model={model}
                value={form?.mention ?? ''}
                onSelect={(value) =>
                  setForm((prev) =>
                    prev
                      ? {
                          ...prev,
                          mention: value,
                          error: validateForm(value),
                        }
                      : prev,
                  )
                }
              />
            </div>

            <div className="space-y-1">
              <LabelWithoutRef>
                Valor default <span className="text-xs text-muted-foreground">opcional</span>
              </LabelWithoutRef>
              <Input
                autoFocus
                placeholder="Ej. Plus, Inversor, gbm@email.com"
                value={form?.defaultValue ?? ''}
                onChange={(e) => {
                  const sanitized = e.target.value.replace(/'/g, '');

                  setForm((prev) =>
                    prev
                      ? {
                          ...prev,
                          defaultValue: sanitized,
                          error: validateForm(prev.mention),
                        }
                      : prev,
                  );
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleConfirm();
                }}
              />
              {form?.error && <p className="text-sm text-destructive">{form.error}</p>}
            </div>

            <div className="mt-10 flex flex-col-reverse gap-4 sm:flex-row sm:justify-end">
              <Button
                title="Cancelar"
                className="min-w-32"
                variant="destructive"
                onClick={() => {
                  setOpenDialog(false);
                  setForm(null);
                }}
              >
                Cancelar
              </Button>
              <Button
                title="Confirmar"
                className="min-w-32"
                disabled={form?.error !== ''}
                onClick={handleConfirm}
              >
                Confirmar
              </Button>
            </div>
          </section>
        </DialogContent>
      </Dialog>
    </div>
  );
});
